import { memo, useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type AssetCustomFieldValue } from '@air/api/types';

import {
  CustomFieldDateInput,
  type CustomFieldDateInputProps,
} from '~/components/CustomFields/CustomFieldInput/CustomFieldDateInput';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useUpdateItemsCustomFieldValue } from '~/utils/CustomFields/useUpdateItemsCustomFieldValue';

export type PrivateAssetCustomFieldDateInputProps = {
  field: AssetCustomFieldValue;
  trackingLocation: AirActionTrackingLocation;
  canEditCustomFields: boolean;
  assetId: string;
} & Omit<CustomFieldDateInputProps, 'onChange'>;

export const PrivateAssetCustomFieldDateInput = memo(
  ({ field, trackingLocation, canEditCustomFields, assetId, ...rest }: PrivateAssetCustomFieldDateInputProps) => {
    const { updateItemsCustomFieldValue } = useUpdateItemsCustomFieldValue();
    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceId = currentWorkspace?.id;

    const onChange: CustomFieldDateInputProps['onChange'] = useCallback(
      async (date: Date | null) => {
        if (!workspaceId) {
          return;
        }

        await updateItemsCustomFieldValue({
          assetIds: [assetId],
          baseCustomField: field,
          nextDateValue: date?.toISOString(),
          trackingLocation,
          workspaceId,
        });
      },
      [assetId, field, trackingLocation, updateItemsCustomFieldValue, workspaceId],
    );

    return (
      <CustomFieldDateInput {...rest} field={field} onChange={onChange} canEditCustomFields={canEditCustomFields} />
    );
  },
);

PrivateAssetCustomFieldDateInput.displayName = 'PrivateAssetCustomFieldDateInput';
